import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePageTitle } from '../hooks/usePageTitle';
import { movieApi } from '../api/client';
import { toCategory } from '../lib/catalogue';
import { GenreTile } from '../components/genre-tile';
import { PageMasthead } from '../components/page-masthead';
import { TitleMeta } from '../components/title-meta';
import { SubscribeBand } from '../components/subscribe-band';

export default function CategoriesPage() {
  usePageTitle('Categories · Kast', 'Every genre on Kast. Pick one and see what is streaming in it.');
  const { data: rawCategories = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data } = await movieApi.getCategories();
      if (!data.success) return [];
      return data.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const categories = useMemo(() => {
    return (rawCategories || []).map(toCategory).filter(Boolean);
  }, [rawCategories]);

  const stocked = useMemo(() => categories.filter((c) => c.count > 0), [categories]);

  return (
    <div className="flex flex-col min-h-screen">
      <PageMasthead
        title="Categories"
        lead="Kast is organised by genre. Browse films and series by category to find your next watch."
        meta={
          categories.length > 0 ? (
            <TitleMeta
              className="text-sm"
              items={[
                `${categories.length} genres`,
                `${stocked.length} with titles`,
              ]}
            />
          ) : null
        }
      />

      <div className="mx-auto max-w-[1600px] w-full gutter pb-16 sm:pb-24">
        {isLoading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 lg:gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[4/3] rounded-2xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : stocked.length > 0 ? (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 lg:gap-5 list-none p-0">
            {stocked.map((category) => (
              <li key={category.id}>
                <GenreTile
                  category={category}
                  sizes="(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 24vw"
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className="max-w-[50ch] text-base text-muted-foreground">
            No genres available at the moment. Please check back later.
          </p>
        )}
      </div>

      <SubscribeBand />
    </div>
  );
}
